import { fetchRosters } from '@/lib/request/fetchRosters';
import type { Roster, Rosters } from '@/lib/request/types/roster';
import { defineStore } from 'pinia';
import { useUserStore } from '@/stores/user';
import { useTimesheetStore, BreakState } from '@/stores/timesheets';
import { DateTime } from 'luxon';
import { fetchUserRosters } from '@/lib/request/fetchUserRosters';

export function toRostersEmployeeMap(data: Rosters): RosterStoreData {
  return data.reduce(
    (map: RosterStoreData, record: Roster) => ({
      ...map,
      [record.Employee]: [...(map[record.Employee] || []), record],
    }),
    {}
  );
}

export interface RosterStoreStatus {
  isLoading: boolean;
}

export type RosterStoreData = {
  [employeeId: number]: Rosters;
};

export interface RosterStoreErrors {
  loadingError: Error | null;
}

export interface RosterStore {
  data: RosterStoreData;
  status: RosterStoreStatus;
  errors: RosterStoreErrors;
}

export const useRosterStore = defineStore({
  id: 'roster',
  state: (): RosterStore => ({
    data: [],
    status: {
      isLoading: false,
    },
    errors: {
      loadingError: null,
    },
  }),
  getters: {
    isLoading: (state) => {
      return state.status.isLoading;
    },
    getRostersByEmployeeId(state) {
      return (id: number): Rosters => {
        return state.data[id] || [];
      };
    },
    getNextRostersByEmployeeId(state) {
      const startOfToday = DateTime.now().startOf('day').toUnixInteger();
      const endOfToday = DateTime.now().endOf('day').toUnixInteger();
      const nowPlusSixHours = DateTime.now().plus({ hours: 6 }).toUnixInteger();
      const nowMinusSixHours = DateTime.now()
        .minus({ hours: 6 })
        .toUnixInteger();

      const minStart = Math.min(startOfToday, nowMinusSixHours);
      const maxStart = Math.max(endOfToday, nowPlusSixHours);

      return (id: number): Rosters => {
        return (
          state.data[id]?.filter((roster) => {
            if (roster.MatchedByTimesheet !== 0) {
              return false;
            }

            const lateRostersFromPast =
              roster.EndTime > minStart && roster.EndTime < endOfToday;

            const todaysRosters =
              roster.StartTime >= startOfToday && roster.StartTime < maxStart;

            return lateRostersFromPast || todaysRosters;
          }) || []
        );
      };
    },
    getNextRosterForActiveUser() {
      const SECONDS_IN_HOUR = 3600;
      const ACTIVE_ROSTER_MAX_TIME_DIFFERENCE = 8 * SECONDS_IN_HOUR;
      const userStore = useUserStore();
      const rostersNotStarted = this.getRostersByEmployeeId(
        userStore.activeUserOrThrow.Id
      )?.filter(
        ({ MatchedByTimesheet, EndTimeLocalized, StartTimeLocalized }) =>
          MatchedByTimesheet === 0 &&
          DateTime.now().toSeconds() -
            DateTime.fromISO(EndTimeLocalized).toSeconds() <
            ACTIVE_ROSTER_MAX_TIME_DIFFERENCE &&
          DateTime.fromISO(StartTimeLocalized).toSeconds() -
            DateTime.now().toSeconds() <
            ACTIVE_ROSTER_MAX_TIME_DIFFERENCE
      );

      return () => (rostersNotStarted ? rostersNotStarted[0] : null);
    },
    getRosterForActiveTimesheet() {
      const userStore = useUserStore();
      const timesheetStore = useTimesheetStore();
      return () =>
        this.getRostersByEmployeeId(userStore.activeUserOrThrow.Id)?.find(
          ({ Id }) => Id === timesheetStore.activeTimesheet?.Roster
        );
    },
    getStartRosterTime() {
      const startTime =
        this.getRosterForActiveTimesheet()?.StartTime ||
        this.getNextRosterForActiveUser()?.StartTime;

      return () => (startTime ? startTime : null);
    },
    getEndRosterTime() {
      const endTime = this.getRosterForActiveTimesheet()
        ? this.getRosterForActiveTimesheet()?.EndTime
        : this.getNextRosterForActiveUser()?.EndTime;

      return () => (endTime ? endTime : null);
    },
    getRosterLateTimeInMinutes() {
      const SECONDS_IN_MINUTE = 60;

      const startTime = this.getNextRosterForActiveUser()?.StartTime;
      const currentTime = DateTime.now().toSeconds();

      return () =>
        Math.round((currentTime - (startTime || 0)) / SECONDS_IN_MINUTE);
    },
    getLateTimeIntegerHours() {
      const timeInMinutes = this.getRosterLateTimeInMinutes();
      return () => (timeInMinutes < 60 ? 0 : Math.floor(timeInMinutes / 60));
    },
    getLateTimeRemainderMinutes() {
      const timeInMinutes = this.getRosterLateTimeInMinutes();
      return () => (timeInMinutes < 60 ? timeInMinutes : timeInMinutes % 60);
    },
    getScheduledBreaks() {
      const roster = this.getRosterForActiveTimesheet()
        ? this.getRosterForActiveTimesheet()
        : this.getNextRosterForActiveUser();

      return () =>
        roster?.Slots.filter(
          ({ intUnixStart, intUnixEnd }) => intUnixEnd - intUnixStart != 0
        ) || [];
    },
    getAllScheduledBreaks() {
      const roster = this.getRosterForActiveTimesheet()
        ? this.getRosterForActiveTimesheet()
        : this.getNextRosterForActiveUser();

      return () => roster?.Slots || [];
    },
    getInvalidAndValidScheduledBreaksCount() {
      const timesheetStore = useTimesheetStore();
      const roster =
        this.getRosterForActiveTimesheet() || this.getNextRosterForActiveUser();

      return () =>
        !(
          !this.getRosterForActiveTimesheet() && timesheetStore.activeTimesheet
        ) && roster?.Slots?.length
          ? roster?.Slots?.length
          : 0;
    },
    getScheduledBreakStartTimeById() {
      return (breakId: number) =>
        this.getScheduledBreaks()[breakId]?.strState !=
        BreakState.ScheduledDuration
          ? this.getScheduledBreaks()[breakId]?.intUnixStart
          : null;
    },
    getScheduledBreakEndTimeById() {
      return (breakId: number) =>
        this.getScheduledBreaks()[breakId]?.strState !=
        BreakState.ScheduledDuration
          ? this.getScheduledBreaks()[breakId]?.intUnixEnd
          : null;
    },
    getRosterForActiveUser() {
      const timesheets = useTimesheetStore();
      return () =>
        timesheets.isOnShift
          ? this.getRosterForActiveTimesheet()
          : this.getNextRosterForActiveUser();
    },
  },
  actions: {
    async load() {
      this.errors.loadingError = null;
      this.status.isLoading = true;

      try {
        this.data = toRostersEmployeeMap(await fetchRosters());
      } catch (e) {
        if (e instanceof Error) {
          this.errors.loadingError = e;
          throw e;
        }
      } finally {
        this.status.isLoading = false;
      }
    },
    async loadForActiveUser() {
      const userStore = useUserStore();
      this.errors.loadingError = null;
      this.status.isLoading = true;

      try {
        this.data[userStore.activeUserOrThrow.Id] = await fetchUserRosters(
          userStore.data.kioskApiAuthorization,
          DateTime.now().minus({ days: 1 }).toISODate(),
          DateTime.now().plus({ days: 1 }).toISODate()
        );
      } catch (e) {
        if (e instanceof Error) {
          this.errors.loadingError = e;
          throw e;
        }
      } finally {
        this.status.isLoading = false;
      }
    },
  },
});
